import React, {useState, useEffect} from 'react'
import {Formik} from "formik";
import * as yup from 'yup';
import {SuccessMessage} from "../components/Messages";
import ProgressBar from '../../progress-bar.js';
import {FAQLink, FormSubmissionError, RequiredFieldMessage, TermsConditionsLink} from "../components/FormElements";
import CheckboxField from "../components/CheckboxField";
import {
    EmailValidation,
    FirstNameValidation,
    LastNameValidation,
    MobileValidation,
    RecaptchaValidation
} from "../components/FormValidations";
import InputField from "../components/InputField";
import NumberField from "../components/NumberField";
import TextareaField from "../components/TextareaField";
import handleFormSubmit, {localLegendEndpoint} from "../client-api/ANZAPI";
import RecapthaField from "../components/RecapthaField";
import {removePageClassContent} from "../../script";
import _ from "lodash";
import {updateLinkTrackingFields} from "../components/LinkTracking";

const FIRST_NAME_FIELD = 'FirstName';
const LAST_NAME_FIELD = 'LastName';
const MOBILE_FIELD = 'Mobile';
const EMAIL_FIELD = 'Email';
const NAME_ORGANISATION_FIELD = 'NameOrganisation';
const NOMINEE_CONTRIBUTION_FIELD = 'NomineeContribution';
const UNDER_SIXTEEN_FIELD = 'UnderSixteen';
const SIGNUP_FIELD = 'SignUp';
const RECAPTCHA_FIELD = 'Recaptcha';

const initialValues = {
    [FIRST_NAME_FIELD]: '',
    [LAST_NAME_FIELD]: '',
    [MOBILE_FIELD]: '',
    [EMAIL_FIELD]: '',
    [NAME_ORGANISATION_FIELD]: '',
    [NOMINEE_CONTRIBUTION_FIELD]: '',
    [UNDER_SIXTEEN_FIELD]: false,
    [SIGNUP_FIELD]: false,
    [RECAPTCHA_FIELD]: ''
}

const submissionValidation = yup.object().shape({
    [FIRST_NAME_FIELD]: FirstNameValidation,
    [LAST_NAME_FIELD]: LastNameValidation,
    [MOBILE_FIELD]: MobileValidation,
    [EMAIL_FIELD]: EmailValidation,
    [NAME_ORGANISATION_FIELD]: yup.string().required('Organisation, club or school you are affiliated with is required.'),
    [NOMINEE_CONTRIBUTION_FIELD]: yup.string().required('About your nominee and their contribution to your netball community is required'),
    [RECAPTCHA_FIELD]: RecaptchaValidation,
});

const applicationConfirmationTracking = {
    pageName: 'anzcourtside:local-legends:entry-form:confirmation',
    formName: 'anzcourtside:local-legends',
    formStatus: 'submitted',
    journeyStepName: 'confirmation',
    journeyName: 'competition:netball_local_legends',
    journeyStatus: 'complete'
}

const LocalLegendForm = (props) => {
    const [successSubmitted, setSuccessSubmitted] = useState(false);
    const [submissionError, setSubmissionError] = useState(null);

    useEffect(() => {
        ProgressBar()
    }, [])

    return (
        <>
            {successSubmitted === true ? (
                <SuccessMessage
                    heading={'Thank you for your Local Legends entry'}
                    message={'Your entry has been received – good luck!'}
                />
            ) : (
                <>
                    <RequiredFieldMessage/>
                    <hr/>
                    <Formik
                        initialValues={{...initialValues}}
                        initialErrors={{...initialValues}}
                        onSubmit={(values, {setSubmitting, resetForm}) => {
                            handleFormSubmit(values, localLegendEndpoint(props.elementId))
                                .then((res) => {
                                    setSubmitting(false)
                                    if (res.success) {
                                        ProgressBar(2)
                                        resetForm({...initialValues});
                                        setSuccessSubmitted(true);
                                        removePageClassContent('.local-legend-content')
                                        updateLinkTrackingFields(applicationConfirmationTracking);
                                        pageView();

                                        return;
                                    }

                                    setSubmissionError(res?.message)
                                    $('html, body').animate({
                                        'scrollTop': document.getElementById("submissionErrorId").offsetTop - 50
                                    }, 500);
                                })
                        }}
                        validationSchema={submissionValidation}
                    >{(formikProps) => (
                        <>
                            {submissionError !== null && (
                                <div id={'submissionErrorId'}>
                                    <FormSubmissionError message={submissionError}/>
                                </div>
                            )}

                            <form onSubmit={formikProps.handleSubmit}>
                                <div className={`form-group`}>
                                    <InputField
                                        id={'firstName'}
                                        label={'First name'}
                                        name={FIRST_NAME_FIELD}
                                        required={true}
                                        maxLength={30}
                                        {...formikProps}
                                    />

                                    <InputField
                                        id={'lastName'}
                                        label={'Last name'}
                                        name={LAST_NAME_FIELD}
                                        required={true}
                                        maxLength={30}
                                        {...formikProps}
                                    />

                                    <NumberField
                                        name={MOBILE_FIELD}
                                        label={'Mobile number'}
                                        id={'mobile'}
                                        allowNegative={false}
                                        className={'short'}
                                        maxLength={20}
                                        required={true}
                                    />

                                    <InputField
                                        id={'email'}
                                        label={'Email'}
                                        name={EMAIL_FIELD}
                                        required={true}
                                        maxLength={129}
                                        {...formikProps}
                                    />

                                    <InputField
                                        id={'organisation'}
                                        label={'Name of netball organisation, club or school you are affiliated with'}
                                        name={NAME_ORGANISATION_FIELD}
                                        maxLength={125}
                                        required={true}
                                        fieldLength={'full'}
                                        {...formikProps}
                                    />

                                    <TextareaField
                                        label={'Tell us about your nominee and their contribution to your netball community'}
                                        name={NOMINEE_CONTRIBUTION_FIELD}
                                        id={'nominee'}
                                        placeholder={'2000 characters or 500 words'}
                                        maxLength={2000}
                                        fieldLength={'full'}
                                        classNameWrapper={'pb-4'}
                                        required={true}
                                        {...formikProps}
                                    />

                                    <div className={'form-group mt-2'}>
                                        <CheckboxField
                                            name={UNDER_SIXTEEN_FIELD}
                                            label={'If you are under 16, please get your parent or guardian to confirm that you have their consent to nominate'}
                                            values={formikProps.values[UNDER_SIXTEEN_FIELD]}
                                        />
                                    </div>

                                    <div className={'form-group mt-2'}>
                                        <CheckboxField
                                            name={SIGNUP_FIELD}
                                            value={formikProps.values[SIGNUP_FIELD]}
                                            label={'Tick here to sign up to receive news, updates and exclusive offers from ANZ Courtside'}
                                        />
                                    </div>

                                    <div className={'pt-4'}>
                                        <RecapthaField name={RECAPTCHA_FIELD}/>
                                    </div>
                                    <hr/>
                                </div>

                                <div className={'links pt-0'}>
                                    <button
                                        type="submit"
                                        className={'btn btn-primary mr-5'}
                                        disabled={formikProps.isSubmitting || !_.isEmpty(formikProps.errors)}
                                    >Submit
                                    </button>
                                    <FAQLink target={'JudgingLocalLegend'}/>
                                    <TermsConditionsLink target={'TCLocalLegend'}/>
                                </div>
                            </form>
                        </>
                    )}
                    </Formik>
                </>
            )}

        </>
    )
}

export default LocalLegendForm;
